import React, { useState, lazy, Suspense } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Layers,
  Bot,
  Smartphone,
  Sparkles,
  Cloud,
  BarChart3,
  Shield,
  Headphones,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const CalendarModal = lazy(() => import('./CalendarModal'));

const Services = () => {
  const { t, language } = useLanguage();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const coreSolutions = [
    {
      id: 'products',
      icon: Layers,
      title: t.services.core.products.title,
      description: t.services.core.products.description,
      tags: t.services.core.products.tags,
      badge: 'SaaS & Web',
      accentColor: '#4F46E5',
      accentBg: '#EEF2FF'
    },
    {
      id: 'automation',
      icon: Bot,
      title: t.services.core.automation.title,
      description: t.services.core.automation.description,
      tags: t.services.core.automation.tags,
      badge: language === 'es' ? 'Agentes & IA 24/7' : 'AI Agents 24/7',
      accentColor: '#10B981',
      accentBg: '#ECFDF5'
    },
    {
      id: 'platforms',
      icon: Smartphone,
      title: t.services.core.platforms.title,
      description: t.services.core.platforms.description,
      tags: t.services.core.platforms.tags,
      badge: 'Mobile & Commerce',
      accentColor: '#06B6D4',
      accentBg: '#ECFEFF'
    },
    {
      id: 'innovation',
      icon: Sparkles,
      title: t.services.core.innovation.title,
      description: t.services.core.innovation.description,
      tags: t.services.core.innovation.tags,
      badge: language === 'es' ? 'Estrategia & UX' : 'Strategy & UX',
      accentColor: '#8B5CF6',
      accentBg: '#F5F3FF'
    }
  ];

  const additionalCapabilities = [
    { icon: Cloud, title: t.services.additional.cloud.title, description: t.services.additional.cloud.description },
    { icon: BarChart3, title: t.services.additional.analytics.title, description: t.services.additional.analytics.description },
    { icon: Shield, title: t.services.additional.security.title, description: t.services.additional.security.description },
    { icon: Headphones, title: t.services.additional.support.title, description: t.services.additional.support.description }
  ];

  return (
    <>
      <section
        id="services"
        data-testid="services-section"
        className="relative py-20 lg:py-28 overflow-hidden bg-[#FAFAFA] border-t border-slate-100"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="stripe-badge mb-4 inline-flex">
              <span>⚡</span>
              <span>{t.services.badge}</span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mb-4 tracking-tight text-[#0F172A]">
              {t.services.title}{' '}
              <span className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">
                {t.services.titleHighlight}
              </span>
            </h2>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed font-normal">
              {t.services.subtitle}
            </p>
          </div>

          {/* Core Solutions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-14">
            {coreSolutions.map((sol) => {
              const Icon = sol.icon;
              return (
                <div
                  key={sol.id}
                  data-testid={`core-solution-${sol.id}`}
                  className="stripe-card p-8 lg:p-9 flex flex-col justify-between group"
                >
                  <div>
                    {/* Top: Icon + Badge */}
                    <div className="flex items-start justify-between mb-5">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm"
                        style={{ background: sol.accentBg }}
                      >
                        <Icon className="w-6 h-6" style={{ color: sol.accentColor }} />
                      </div>
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold tracking-wide"
                        style={{ background: sol.accentBg, color: sol.accentColor }}
                      >
                        {sol.badge}
                      </span>
                    </div>

                    <h3 className="font-heading font-bold text-xl lg:text-2xl text-[#0F172A] mb-3 group-hover:text-[#4F46E5] transition-colors">
                      {sol.title}
                    </h3>
                    <p className="text-slate-600 text-sm lg:text-base leading-relaxed mb-6 font-normal">
                      {sol.description}
                    </p>
                  </div>

                  {/* Tags */}
                  <div className="pt-4 border-t border-slate-100 flex flex-wrap gap-2">
                    {sol.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-50 text-slate-600 text-xs font-semibold border border-slate-100"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
                        <span>{tag}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Capabilities */}
          <div className="mb-14">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <h3 className="font-heading font-bold text-xl text-[#0F172A]">
                {language === 'es'
                  ? 'Ingeniería, Infraestructura Cloud y Soporte Continuo'
                  : 'Engineering, Cloud Architecture & Ongoing Support'}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {additionalCapabilities.map((cap, idx) => {
                const Icon = cap.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 rounded-2xl bg-white border border-slate-200/80 hover:border-[#4F46E5]/40 hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-[#4F46E5] mb-4">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h4 className="font-heading font-bold text-sm text-[#0F172A] mb-1.5">
                      {cap.title}
                    </h4>
                    <p className="text-slate-500 text-xs leading-relaxed">
                      {cap.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Banner */}
          <div className="p-8 lg:p-12 rounded-3xl bg-[#0F172A] text-white shadow-xl flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/90 text-xs font-semibold mb-3 border border-white/10">
                <Sparkles className="w-3.5 h-3.5 text-[#38BDF8]" />
                {language === 'es' ? 'Prototipo Inicial Sin Costo' : 'Free Initial Prototype'}
              </div>
              <h3 className="font-heading font-bold text-2xl lg:text-3xl mb-2">
                {language === 'es'
                  ? '¿Tienes una idea o proceso que digitalizar?'
                  : 'Have an idea or process to digitize?'}
              </h3>
              <p className="text-slate-300 text-sm lg:text-base max-w-2xl font-normal">
                {language === 'es'
                  ? 'Conversamos 30 minutos sobre tus objetivos y te entregamos una propuesta con prototipo funcional sin costo ni compromiso.'
                  : 'We spend 30 minutes on your goals and deliver a proposal with a functional prototype, no commitment.'}
              </p>
            </div>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="shrink-0 btn-stripe-white"
            >
              <span>{language === 'es' ? 'Agendar una Reunión' : 'Schedule a Meeting'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

        </div>
      </section>

      {isCalendarOpen && (
        <Suspense fallback={null}>
          <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default Services;
